import React from 'react';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import moment from 'moment';
import {renderDuration} from 'lib/timeUtils';
import {setUpStore} from 'client/framework';
import {resource} from '@albert-io/json-api-framework/request/builder';
import SettingsTableStore from 'sg/Tables/SettingsTable/SettingsTable.store';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import PreviousAttempts from 'generic/PreviousAttempts/PreviousAttempts.react';
import classroomInsightsStore from '../../../ClassroomInsights.store';
import EmptyInsightsReport from '../../EmptyInsightsReport/EmptyInsightsReport.react';

const settingsTableStore = setUpStore(SettingsTableStore, 'QuestionInsightsStudentsTableStore');

class StudentsTable extends React.Component {

  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        attempt: PropTypes.string,
        endDate: PropTypes.string,
        guideLevelId: PropTypes.string,
        startDate: PropTypes.string,
        subjectId: PropTypes.string
      })
    }),
    params: PropTypes.shape({
      classId: PropTypes.string,
      questionId: PropTypes.string
    })
  };

  getStudentsQuery() {
    const {
      attempt,
      endDate,
      guideLevelId,
      startDate,
      subjectId
    } = this.props.location.query;
    const guessContext = {};
    if (attempt === 'mostRecent') {
      guessContext.most_recent = true;
    } else {
      guessContext.attempt_number = attempt;
    }

    return resource('students_v2')
      .mandarkEndpoint(['classrooms_v1', this.props.params.classId, 'students_v2'])
      .withMeta('student_v2')
      .meta({
        context: {
          end_date: endDate,
          guess: guessContext,
          question: {
            id: this.props.params.questionId
          },
          start_date: startDate,
          subject: {
            ids: subjectId
          }
        }
      })
      .meta({
        context: {
          guideLevel: {
            id: guideLevelId
          }
        }
      }, guideLevelId);
  }

  renderName(student) {
    return student.getFullName();
  }

  renderResult = (student) => {
    if (student.getMeta().getCountOfGuesses() === 0) {
      return 'N/A';
    }

    const attempt = this.props.location.query.attempt;
    let {startDate, endDate} = this.props.location.query;
    /**
     * If 'Most Recent' is selected for the attempt, we want to include attempts within the selected date range
     * If a specific attempt is selected, we want to only include that specific attempt
     */
    const isMostRecentAttempt = attempt === 'mostRecent';
    const isCorrect = Boolean(student.getMeta().getCountOfCorrectGuesses());
    startDate = isMostRecentAttempt && startDate
      ? startDate
      : null;
    endDate = isMostRecentAttempt && endDate
      ? endDate
      : null;

    return (
      <PreviousAttempts
        specificAttemptNumberToShow={isMostRecentAttempt ? null : attempt}
        initialIsCorrectStatus={isCorrect}
        questionId={this.props.params.questionId}
        showTooltip
        storeName={`question-drilldown-${student.getId()}`}
        userId={student.getId()}
        startDate={startDate}
        endDate={endDate}
        disableAttemptLink
      />
    );
  };

  renderTimeSpent(student) {
    if (student.getMeta().getCountOfGuesses() === 0) {
      return 'N/A';
    }

    const timeSpent = moment.duration(student.getMeta().getSumOfGuessesTimeElapsed());
    return renderDuration(timeSpent, {style: 'hh:mm:ss'});
  }

  render() {
    const emptyResultsHeader = settingsTableStore.getSearchTerm()
      ? `No students matching your search term have answered this question.`
      : 'No students have answered this question yet.';

    const emptyResultsContent = settingsTableStore.getSearchTerm()
      ? 'Once at least 1 student matching your search term has answered this question, those students will appear here.'
      : 'Once at least 1 student has answered this question, those students will appear here.';

    return (
      <SettingsTable
        emptyTableElement={(
          <EmptyInsightsReport
            header={emptyResultsHeader}
            content={emptyResultsContent}
          />
        )}
        query={this.getStudentsQuery()}
        searchFilters={classroomInsightsStore.getStudentsSearchFilters}
        storeName={settingsTableStore.getName()}
      >
        <Column
          defaultSortParam
          defaultVisible
          content={this.renderName}
          heading='Name'
          sortBy='last_name'
        />
        <Column
          align='right'
          defaultVisible
          content={this.renderTimeSpent}
          heading='Time Spent'
          sortBy='meta.sum_of_guesses_time_elapsed'
        />
        <Column
          align='center'
          content={this.renderResult}
          defaultVisible={true}
          heading='Result'
          sortBy='meta.count_of_guesses'
        />
      </SettingsTable>
    );
  }
}

export default withRouter(StudentsTable);
